<script lang="ts">
  import { onMount } from "svelte";
  import { i18n } from "../i18n.svelte";
  import { inview } from "../actions";
  import SectionHead from "./SectionHead.svelte";
  import Sprout from "./Sprout.svelte";
  import { demo } from "../demo";

  const d = $derived(i18n.t.demo);
  const set = $derived(demo[i18n.locale]);

  const TARGET = 5;

  let subjectId = $state("english");
  let level = $state(2);
  let mastery = $state(28);
  let answered = $state<number[]>([]);
  let currentIdx = $state(-1);
  let picked = $state<number | null>(null);
  let checked = $state(false);
  let hintOpen = $state(false);
  let done = $state(false);

  const current = $derived(
    currentIdx >= 0 ? set[subjectId].questions[currentIdx] : null,
  );
  const growth = $derived(
    mastery > 75 ? 4 : mastery > 45 ? 3 : mastery > 20 ? 2 : 1,
  );
  const isCorrect = $derived(
    checked && current != null && picked === current.answer,
  );

  function pickNext() {
    const pool = set[subjectId].questions;
    const remaining = pool
      .map((q, idx) => ({ q, idx }))
      .filter((x) => !answered.includes(x.idx));
    if (answered.length >= TARGET || remaining.length === 0) {
      done = true;
      currentIdx = -1;
      return;
    }
    remaining.sort(
      (a, b) =>
        Math.abs(a.q.level - level) - Math.abs(b.q.level - level) ||
        a.q.level - b.q.level,
    );
    currentIdx = remaining[0].idx;
    picked = null;
    checked = false;
    hintOpen = false;
  }

  function check() {
    if (picked == null || current == null || checked) return;
    checked = true;
    answered = [...answered, currentIdx];
    if (picked === current.answer) {
      mastery = Math.min(100, mastery + 12);
      level = Math.min(5, level + 1);
    } else {
      mastery = Math.min(100, mastery + 3);
      level = Math.max(1, level - 1);
      hintOpen = true;
    }
  }

  function restart() {
    level = 2;
    mastery = 28;
    answered = [];
    done = false;
    picked = null;
    checked = false;
    hintOpen = false;
    chatLog = [];
    usedChips = [];
    pickNext();
  }

  function selectSubject(id: string) {
    if (id === subjectId) return;
    subjectId = id;
    restart();
  }

  // --- tutor chat ---
  let chatLog = $state<{ role: "user" | "bot"; text: string }[]>([]);
  let typing = $state(false);
  let usedChips = $state<number[]>([]);

  function askChip(i: number) {
    if (typing) return;
    const turn = set[subjectId].chat[i];
    chatLog = [...chatLog, { role: "user", text: turn.q }];
    usedChips = [...usedChips, i];
    typing = true;
    setTimeout(() => {
      chatLog = [...chatLog, { role: "bot", text: turn.a }];
      typing = false;
    }, 950);
  }

  onMount(restart);
</script>

<section class="section section--rule" id="demo">
  <div class="container">
    <SectionHead label={d.label} title={d.title} lead={d.lead} align="center" />

    <div class="tabs reveal" use:inview>
      {#each d.subjects as s}
        <button
          class="tab"
          class:active={s.id === subjectId}
          onclick={() => selectSubject(s.id)}
        >
          {s.label}
        </button>
      {/each}
    </div>

    <div class="stage">
      <!-- engine -->
      <div class="panel engine reveal" use:inview={{ delay: 80 }}>
        <div class="meters">
          <div class="m">
            <span class="ml">{d.difficulty}</span>
            <div class="diff">
              {#each [1, 2, 3, 4, 5] as n}
                <i class:on={n <= level}></i>
              {/each}
            </div>
            <span class="mhint">{level <= 2 ? d.diffEasy : level >= 4 ? d.diffHard : ""}</span>
          </div>
          <div class="m grow">
            <Sprout size={30} stage={growth as 1 | 2 | 3 | 4} />
            <div class="mwrap">
              <div class="mrow"><span>{d.mastery}</span><span>{mastery}%</span></div>
              <div class="track"><div class="fill" style="width:{mastery}%"></div></div>
            </div>
          </div>
        </div>

        {#if done}
          <div class="done">
            <Sprout size={64} stage={4} soil />
            <h3>{d.done}</h3>
            <p>{d.doneNote}</p>
            <button class="btn btn-primary" onclick={restart}>{d.restart}</button>
          </div>
        {:else if current}
          <div class="qbox">
            <span class="qn">Q{answered.length + 1} / {TARGET}</span>
            <p class="q">{current.q}</p>
          </div>

          <div class="opts">
            {#each current.options as opt, i}
              <button
                class="opt"
                class:sel={picked === i && !checked}
                class:ok={checked && i === current.answer}
                class:no={checked && picked === i && i !== current.answer}
                disabled={checked}
                onclick={() => (picked = i)}
              >
                <span class="dot"></span>
                <span class="ot">{opt}</span>
                {#if checked && i === current.answer}
                  <svg viewBox="0 0 16 16" width="16" height="16"><path d="M3 8.5l3 3 7-8" stroke="currentColor" stroke-width="2.2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
                {/if}
              </button>
            {/each}
          </div>

          {#if hintOpen && !checked}
            <p class="hint"><strong>{d.hintLabel}:</strong> {current.hint}</p>
          {/if}

          {#if checked}
            <div class="verdict" class:good={isCorrect}>
              <strong>{isCorrect ? d.correct : d.incorrect}</strong>
              <span>{current.explain}</span>
            </div>
          {/if}

          <div class="controls">
            {#if !checked}
              <button class="link" onclick={() => (hintOpen = !hintOpen)}>{d.showHint}</button>
              <button class="btn btn-primary" disabled={picked == null} onclick={check}>
                {d.check}
              </button>
            {:else}
              <span></span>
              <button class="btn btn-primary" onclick={pickNext}>
                {d.next}<span class="arrow">→</span>
              </button>
            {/if}
          </div>
        {/if}
      </div>

      <!-- tutor chat -->
      <div class="panel chat reveal" use:inview={{ delay: 180 }}>
        <div class="chead">
          <span class="ava"><Sprout size={20} stage={2} /></span>
          <div>
            <strong>{d.chatTitle}</strong>
            <span class="on"><i></i>online</span>
          </div>
        </div>

        <div class="clog">
          {#if chatLog.length === 0}
            <p class="empty">{d.chatHint}</p>
          {/if}
          {#each chatLog as msg}
            <div class="cmsg" class:user={msg.role === "user"}>
              {#if msg.role === "bot"}<span class="bava"><Sprout size={14} stage={2} /></span>{/if}
              <p>{msg.text}</p>
            </div>
          {/each}
          {#if typing}
            <div class="cmsg">
              <span class="bava"><Sprout size={14} stage={2} /></span>
              <p class="typing"><span></span><span></span><span></span></p>
            </div>
          {/if}
        </div>

        <div class="chips">
          {#each set[subjectId].chat as turn, i}
            {#if !usedChips.includes(i)}
              <button onclick={() => askChip(i)}>{turn.q}</button>
            {/if}
          {/each}
        </div>
      </div>
    </div>
  </div>
</section>

<style>
  .tabs {
    margin: clamp(2rem, 1.5rem + 2vw, 3rem) auto 0;
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  .tab {
    font-weight: 700;
    font-size: 0.92rem;
    color: var(--ink-soft);
    padding: 0.6em 1.3em;
    border-radius: 999px;
    border: 1px solid var(--line-strong);
    transition: all 0.3s var(--ease);
  }
  .tab.active {
    background: var(--green);
    border-color: var(--green);
    color: #fff;
  }

  .stage {
    margin-top: 2rem;
    display: grid;
    grid-template-columns: 1.15fr 0.85fr;
    gap: 1.2rem;
    align-items: start;
  }
  .panel {
    background: var(--paper-card);
    border: 1px solid var(--line);
    border-radius: var(--radius);
    padding: clamp(1.3rem, 1rem + 1vw, 1.8rem);
  }

  /* meters */
  .meters {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding-bottom: 1.2rem;
    border-bottom: 1px solid var(--line);
    flex-wrap: wrap;
  }
  .m {
    display: flex;
    align-items: center;
    gap: 0.6rem;
  }
  .ml {
    font-family: var(--font-mono);
    font-size: 0.7rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--muted);
  }
  .diff {
    display: inline-flex;
    gap: 3px;
  }
  .diff i {
    width: 6px;
    height: 16px;
    border-radius: 2px;
    background: var(--line);
    transition: background 0.4s var(--ease);
  }
  .diff i.on {
    background: var(--green);
  }
  .mhint {
    font-size: 0.72rem;
    color: var(--terra);
  }
  .grow {
    flex: 1;
    min-width: 180px;
    justify-content: flex-end;
  }
  .mwrap {
    width: min(220px, 60%);
  }
  .mrow {
    display: flex;
    justify-content: space-between;
    font-size: 0.74rem;
    color: var(--muted);
    margin-bottom: 4px;
  }
  .track {
    height: 7px;
    border-radius: 999px;
    background: var(--paper-deep);
    overflow: hidden;
  }
  .fill {
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, var(--sprout), var(--green));
    transition: width 0.6s var(--ease);
  }

  .qbox {
    margin-top: 1.3rem;
  }
  .qn {
    font-family: var(--font-mono);
    font-size: 0.72rem;
    letter-spacing: 0.1em;
    color: var(--terra);
  }
  .q {
    margin-top: 0.5rem;
    font-size: clamp(1.15rem, 1rem + 0.6vw, 1.4rem);
    font-weight: 500;
    color: var(--ink);
    line-height: 1.6;
  }

  .opts {
    margin-top: 1.2rem;
    display: grid;
    gap: 0.6rem;
  }
  .opt {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    text-align: left;
    padding: 0.85rem 1rem;
    border: 1px solid var(--line);
    border-radius: 12px;
    background: var(--paper);
    color: var(--ink);
    font-size: 1rem;
    transition: all 0.2s var(--ease);
  }
  .opt:hover:not(:disabled) {
    border-color: var(--green);
  }
  .opt .dot {
    flex: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid var(--line-strong);
    transition: all 0.2s var(--ease);
  }
  .opt.sel {
    border-color: var(--green);
    background: rgba(95, 179, 100, 0.08);
  }
  .opt.sel .dot {
    border-color: var(--green);
    background: var(--green);
  }
  .opt.ok {
    border-color: var(--green);
    background: rgba(95, 179, 100, 0.16);
    color: var(--green-deep);
    font-weight: 700;
  }
  .opt.ok .dot {
    border-color: var(--green);
    background: var(--green);
  }
  .opt.no {
    border-color: var(--terra);
    background: rgba(194, 105, 60, 0.12);
    color: var(--terra-deep);
  }
  .opt.no .dot {
    border-color: var(--terra);
    background: var(--terra);
  }
  .opt .ot {
    flex: 1;
  }

  .hint {
    margin-top: 0.9rem;
    font-size: 0.9rem;
    color: var(--ink-soft);
    background: rgba(194, 105, 60, 0.08);
    border-left: 3px solid var(--terra);
    padding: 0.6rem 0.9rem;
    border-radius: 0 8px 8px 0;
  }
  .verdict {
    margin-top: 1rem;
    padding: 0.9rem 1rem;
    border-radius: 12px;
    background: rgba(194, 105, 60, 0.1);
    border: 1px solid rgba(194, 105, 60, 0.3);
  }
  .verdict.good {
    background: rgba(95, 179, 100, 0.12);
    border-color: rgba(95, 179, 100, 0.4);
  }
  .verdict strong {
    display: block;
    margin-bottom: 0.2rem;
    color: var(--ink);
  }
  .verdict span {
    font-size: 0.92rem;
    color: var(--ink-soft);
  }

  .controls {
    margin-top: 1.3rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }
  .link {
    font-size: 0.9rem;
    color: var(--green-deep);
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  .controls .btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .done {
    text-align: center;
    padding: 1.5rem 1rem;
  }
  .done :global(svg) {
    margin: 0 auto 1rem;
  }
  .done h3 {
    font-size: var(--fs-h3);
  }
  .done p {
    margin: 0.7rem auto 1.5rem;
    max-width: 40ch;
    color: var(--ink-soft);
  }

  /* chat */
  .chat {
    display: flex;
    flex-direction: column;
    min-height: 420px;
  }
  .chead {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--line);
  }
  .chead .ava {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--sprout-soft);
    display: grid;
    place-items: center;
  }
  .chead strong {
    display: block;
    font-size: 0.95rem;
    color: var(--ink);
  }
  .chead .on {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.74rem;
    color: var(--muted);
  }
  .chead .on i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--sprout);
  }

  .clog {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    padding: 1rem 0;
    overflow-y: auto;
  }
  .empty {
    margin: auto;
    font-size: 0.88rem;
    color: var(--muted);
    text-align: center;
  }
  .cmsg {
    display: flex;
    gap: 0.5rem;
    max-width: 90%;
  }
  .cmsg p {
    padding: 0.6rem 0.85rem;
    border-radius: 14px;
    font-size: 0.9rem;
    line-height: 1.6;
  }
  .cmsg.user {
    align-self: flex-end;
  }
  .cmsg.user p {
    background: var(--green);
    color: #fff;
    border-bottom-right-radius: 4px;
  }
  .cmsg:not(.user) p {
    background: var(--paper);
    border: 1px solid var(--line);
    color: var(--ink);
    border-bottom-left-radius: 4px;
  }
  .bava {
    flex: none;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: var(--sprout-soft);
    display: grid;
    place-items: center;
  }
  .typing {
    display: inline-flex !important;
    gap: 4px;
  }
  .typing span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--muted);
    animation: blink 1.2s infinite both;
  }
  .typing span:nth-child(2) { animation-delay: 0.2s; }
  .typing span:nth-child(3) { animation-delay: 0.4s; }
  @keyframes blink {
    0%, 60%, 100% { opacity: 0.25; }
    30% { opacity: 1; }
  }

  .chips {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-top: 1rem;
    border-top: 1px solid var(--line);
  }
  .chips button {
    text-align: left;
    font-size: 0.88rem;
    color: var(--green-deep);
    border: 1px solid var(--sprout);
    background: rgba(95, 179, 100, 0.08);
    padding: 0.6em 0.9em;
    border-radius: 10px;
    transition: background 0.25s var(--ease);
  }
  .chips button:hover {
    background: rgba(95, 179, 100, 0.18);
  }

  @media (max-width: 860px) {
    .stage {
      grid-template-columns: 1fr;
    }
  }
</style>
